import { useEffect, useState, useCallback } from 'react';
import { useMount, useSessionStorage, useUnmount } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { CloudDownloadOutlined, DownOutlined } from '@ant-design/icons';

import PageHeader from '../../uiKitComponents/pageHeader';
import Alert from '../../uiKitComponents/alert';
import Button from '../../uiKitComponents/button';
import Table from '../../uiKitComponents/table';
import Dropdown from '../../uiKitComponents/dropdown';
import FiltersSection from '../../components/filtersSection';
import PaginationSummary from '../../components/PaginationSummary';

import {
  getVerificationQueueReport,
  cleanReports,
  setCleanOnUnmountTrue as setReportsCleanOnUnmountTrue,
} from '../../store/actions/reportPagesAction';
import { getUserFilters } from '../../store/actions/userFiltersAction';
import api from '../../utils/appApi';
import buildTableColumns from '../../utils/buildTableColumns';
import downloadFromBlobData from '../../utils/downloadFromBlobData';
import formattingFilters from '../../utils/formattingFilters';

const initialState = {
  page: 1,
  sort_column: 'id',
  sort_direction: 'descend',
  filters: [{}],
  keyword: undefined,
  filter_info: undefined,
};

const ActionQueuePage = () => {
  const [state, setState] = useSessionStorage('action-queue', {
    ...initialState,
  });
  const {
    page,
    sort_column,
    sort_direction,
    filters,
    keyword,
    filter_info,
  } = state;

  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const { cleanOnUnmount, verificationQueue, savedFilters = [] } = useSelector(
    (store) => ({
      cleanOnUnmount: store.reportPages.cleanOnUnmount,
      verificationQueue: store.reportPages.payload.verificationQueue,
      savedFilters: store.userFilters.payload,
    }),
  );

  useMount(() => {
    setReportsCleanOnUnmountTrue()(dispatch);
  });

  useUnmount(() => {
    if (cleanOnUnmount) {
      sessionStorage.removeItem('action-queue');
      cleanReports()(dispatch);
    }
  });

  const fetchData = useCallback(() => {
    setLoading(true);
    const filterParams = {};
    filters.forEach((filter) =>
      Object.entries(
        formattingFilters(filter, filter.keyword, 'STRING'),
      ).forEach(([key, value]) => {
        filterParams[`filters[${key}]`] = value;
      }),
    );

    getVerificationQueueReport({
      page,
      sort_column,
      sort_direction: sort_direction || 'ascend',
      keyword,
      ...filterParams,
    })(dispatch)
      .catch((err) => {
        if (err?.response?.status === 500) {
          setError({
            status: err.response.status,
            message: err.response.data.message,
          });
          sessionStorage.removeItem('action-queue');
        }
      })
      .finally(() => setLoading(false));
  }, [dispatch, page, sort_column, sort_direction, keyword, filters]);

  useEffect(() => {
    fetchData();
  }, [dispatch, fetchData]);

  const fetchFilters = useCallback(() => {
    getUserFilters('verification-queue')(dispatch).catch((err) => {
      if (err.response?.status === 500) {
        setError({
          status: err.response.status,
          message: err.response.data.message,
        });
      }
    });
  }, [dispatch, setError]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  const selectSavedFilter = (filterId) => {
    const id = Number(filterId);
    const filter = savedFilters.find((f) => f.id === id);
    if (filter) {
      const newFilters = Object.entries(filter.filters).map(([key, value]) =>
        formattingFilters(key, value, 'OBJECT'),
      );
      setState((prevState) => ({
        ...prevState,
        filters: newFilters,
        keyword: filter.keyword,
        filter_info: filter,
      }));
    }
  };

  const onSubmitFilters = (values) =>
    setState((prevState) => ({
      ...prevState,
      ...values,
    }));

  const clearFilters = () => setState(initialState);

  const onTableChange = useCallback(
    (pagination, _, sorter, { action }) => {
      const newState = {};
      if (action === 'paginate') {
        newState.page = pagination.current;
      }
      if (action === 'sort') {
        newState.sort_column = sorter.field;
        newState.sort_direction = sorter.order;
      }
      setState((prevState) => ({ ...prevState, ...newState }));
    },
    [setState],
  );

  const handleExport = useCallback(async () => {
    const filterParams = {};
    filters.forEach((filter) =>
      Object.entries(
        formattingFilters(filter, filter.keyword, 'STRING'),
      ).forEach(([key, value]) => {
        filterParams[`filters[${key}]`] = value;
      }),
    );

    try {
      const response = await api.reportsPages.getVerificationQueueReport({
        page,
        sort_column,
        sort_direction: sort_direction || 'ascend',
        keyword,
        export: 1,
        ...filterParams,
      });
      downloadFromBlobData(response);
    } catch (err) {
      console.error('Error exporting verification queue report:', err);
    }
  }, [page, sort_column, sort_direction, keyword, filters]);

  if (error)
    return (
      <PageHeader className="page-main-header" title="Action Queue">
        <Alert
          showIcon
          type="error"
          message={`Error ${error.status}`}
          description={error.message}
        />
      </PageHeader>
    );

  return (
    <div>
      <PageHeader
        className="page-main-header"
        title="Action Queue"
        extra={[
          <Button
            icon={<CloudDownloadOutlined />}
            onClick={handleExport}
            key="export"
          >
            Export CSV
          </Button>,
          <Dropdown
            menu={{
              items: savedFilters?.map((item) => ({
                label: item.name,
                key: item.id,
              })),
              onClick: ({ key }) => selectSavedFilter(key),
            }}
            trigger={['click']}
            placement="bottomRight"
            disabled={!savedFilters.length}
            key="saved_filters"
          >
            <Button loading={!savedFilters}>
              Saved Filters
              <DownOutlined />
            </Button>
          </Dropdown>,
        ]}
      >
        <FiltersSection
          clearFilters={clearFilters}
          onSubmit={onSubmitFilters}
          filters={filters}
          keyword={keyword}
          columnOptions={verificationQueue?.filterable}
          loading={!verificationQueue?.filterable}
          filter_info={filter_info}
          filter_columns={verificationQueue?.columns}
          onUpdateFilter={selectSavedFilter}
          entity="verification-queue"
        />
      </PageHeader>
      <div className="page-main-content">
        <PaginationSummary meta={verificationQueue?.meta} />
        <Table
          rowKey="id"
          columns={buildTableColumns(verificationQueue?.columns)}
          dataSource={verificationQueue?.data}
          loading={loading}
          onChange={onTableChange}
          pagination={{
            position: ['topRight', 'bottomRight'],
            showSizeChanger: false,
            showQuickJumper: true,
            pageSize: verificationQueue?.meta?.per_page,
            total: verificationQueue?.meta?.total,
            current: verificationQueue?.meta?.current_page,
          }}
        />
        <PaginationSummary meta={verificationQueue?.meta} />
      </div>
    </div>
  );
};

export default ActionQueuePage;
